import { get, html, patch, post } from "../lib.js";
import { Badge, Field, Loading, Modal, PageHeader, toast, useAction, useForm, useLoad } from "../ui.js";
import { useState } from "preact/hooks";
import { locale, tr } from "../i18n.js";
import { STATUS_BADGE } from "./billing.js";
import { PageGuide } from "../guide.js";

const TENANT_STATUS = { active: "Active", suspended: "Suspended" };

/**
 * What happened to an invite: emailed, or, when email couldn't go out, the set-password link to
 * pass on by hand. Shared with the team settings page.
 */
export function InviteOutcome({ invite }) {
  const copy = () => navigator.clipboard.writeText(invite.link).then(() => toast(tr("Copied")));
  if (invite.sent) {
    return html`<div class="callout ok small">${invite.existing_account
      ? tr("{email} already had an account. We emailed them that they were added.", { email: invite.email })
      : tr("Invite sent to {email}. The link to choose a password works for 7 days.", { email: invite.email })}</div>`;
  }
  if (!invite.link) return html`<div class="callout warn small">${tr("{email} already had an account and can sign in with it. We couldn't email them.", { email: invite.email })}</div>`;
  return html`<div class="callout warn small">
    ${tr("We couldn't email {email}. Send them this link yourself; it lets them choose a password, works once and lasts 7 days:", { email: invite.email })}
    <div class="form-actions"><code dir="ltr" style="word-break: break-all">${invite.link}</code><button type="button" class="btn small" onClick=${copy}>${tr("Copy link")}</button></div>
  </div>`;
}

export function AdminPage({ onChange }) {
  const [data, reload] = useLoad(() => get("/admin/tenants"), []);
  const { values, bind, setValues } = useForm({ business_name: "", owner_name: "", owner_email: "", timezone: "Asia/Jerusalem" });
  const [created, setCreated] = useState(null);
  const [managing, setManaging] = useState(null);
  const [budgeting, setBudgeting] = useState(null);
  const [busy, run] = useAction();
  const create = async (e) => {
    e.preventDefault();
    const res = await run(() => post("/admin/tenants", values), tr("Business created"));
    setCreated(res.invite);
    setValues({ business_name: "", owner_name: "", owner_email: "", timezone: "Asia/Jerusalem" });
    reload();
    onChange();
  };
  return html`<div>
    <${PageHeader} title=${tr("All businesses")} subtitle=${tr("Platform view: onboard clients and watch usage")} />
    <${PageGuide} id="admin" />
    <div class="grid-agent">
      <section class="card table-wrap">
        ${!data && html`<${Loading} />`}
        <table class="table">
          <thead><tr><th>${tr("Business")}</th><th>${tr("Plan")}</th><th>${tr("Channels")}</th><th>${tr("Conversations (30d)")}</th><th>${tr("Bookings (30d)")}</th><th>${tr("AI cost this month")}</th><th></th></tr></thead>
          <tbody>${data?.tenants.map((x) => html`<tr>
            <td><strong>${x.name}</strong><div class="muted small">${x.slug}</div></td>
            <td class="small"><${PlanCell} x=${x} onManage=${() => setManaging(x)} /></td>
            <td class="small">${x.channels ?? "—"}</td>
            <td>${x.conversations_30d}</td><td>${x.bookings_30d}</td>
            <td>
              <${BudgetCell} x=${x} onEdit=${() => setBudgeting(x)} />
              <div class="muted small">${tr("{n} tokens (30d)", { n: Number(x.tokens_30d).toLocaleString(locale()) })}</div>
              <div class="muted small">${x.own_ai_key ? tr("Own Anthropic key") : tr("We paid ${amount}", { amount: Number(x.platform_ai_spend_usd ?? 0).toFixed(2) })}</div>
            </td>
            <td class="row-actions"><${Badge} kind=${x.status === "active" ? "ok" : "warn"}>${tr(TENANT_STATUS[x.status] ?? x.status)}</${Badge}>
              ${x.owner_invited_at && html`<${Badge} kind="neutral">${tr("Invite pending")}</${Badge}>`}
              <button class="btn small ghost" onClick=${async () => { await run(() => patch(`/admin/tenants/${x.id}`, { status: x.status === "active" ? "suspended" : "active" })); reload(); }}>${x.status === "active" ? tr("Suspend") : tr("Activate")}</button>
              ${x.owner_email && html`<button class="btn small ghost" title=${x.owner_email} onClick=${async () => { const res = await run(() => post(`/admin/tenants/${x.id}/invite`)); setCreated(res.invite); reload(); }}>${tr("Resend invite")}</button>`}
              <label class="toggle small" title=${tr("Lectures and files on this business's website, each with a page and a QR code")}><input type="checkbox" checked=${!!x.library_enabled} disabled=${busy} onChange=${async (e) => { await run(() => patch(`/admin/tenants/${x.id}`, { library_enabled: e.target.checked }), e.target.checked ? tr("Library switched on") : tr("Library switched off")); reload(); }} /> ${tr("Website library")}</label></td>
          </tr>`)}</tbody>
        </table>
      </section>
      <div>
      <${ClaudeKey} />
      <${PaymentAccount} />
      <form class="card" onSubmit=${create}>
        <h2>${tr("Onboard a business")}</h2>
        <${Field} label=${tr("Business name")}><input required ...${bind("business_name")} /></${Field}>
        <${Field} label=${tr("Owner name")}><input required ...${bind("owner_name")} /></${Field}>
        <${Field} label=${tr("Owner email")}><input type="email" required ...${bind("owner_email")} /></${Field}>
        <${Field} label=${tr("Time zone")}><input required ...${bind("timezone")} /></${Field}>
        <button class="btn primary" disabled=${busy}>${tr("Create")}</button>
        ${created && html`<${InviteOutcome} invite=${created} />`}
      </form>
      </div>
    </div>
    ${managing && html`<${ManageBilling} x=${managing} onClose=${() => setManaging(null)} onDone=${() => { setManaging(null); reload(); }} />`}
    ${budgeting && html`<${AiBudget} x=${budgeting} onClose=${() => setBudgeting(null)} onDone=${() => { setBudgeting(null); reload(); }} />`}
  </div>`;
}

function PlanCell({ x, onManage }) {
  const [kind, label] = STATUS_BADGE[x.billing_status] ?? ["neutral", x.billing_status ?? "—"];
  return html`<div><strong>${x.plan ?? "—"}</strong> <${Badge} kind=${kind}>${tr(label)}</${Badge}></div>
    ${x.billing_status === "trialing" && html`<div class="muted">${tr("until {date}", { date: new Date(x.trial_ends_at).toLocaleDateString(locale()) })}</div>`}
    <button class="btn small ghost" onClick=${onManage}>${tr("Manage")}</button>`;
}

const PLAN_CHOICES = ["starter", "growth", "pro", "founding"];

/** Plan, trial, complimentary status and setup fee for one business. */
function ManageBilling({ x, onClose, onDone }) {
  const paying = ["active", "past_due"].includes(x.billing_status);
  const { values, bind, check } = useForm({ plan: x.plan ?? "growth", days: 14, waive: false });
  const [busy, run] = useAction();
  const billing = (body, msg) => run(() => patch(`/admin/tenants/${x.id}/billing`, body), msg).then(onDone);
  const comp = () => billing({ plan: values.plan, comped: true }, tr("Now complimentary"));
  const trial = () => {
    if (x.billing_status !== "trialing" && !confirm(tr("Start a free trial for {name}? The AI stops when it ends unless they choose a plan.", { name: x.name }))) return;
    billing({ ...(values.plan !== "founding" ? { plan: values.plan } : { plan: "growth" }), extend_trial_days: Number(values.days) }, tr("Trial updated"));
  };
  return html`<${Modal} title=${tr("Billing for {name}", { name: x.name })} onClose=${onClose}>
    <p class="small">${tr("Now")}: <strong>${x.plan ?? "—"}</strong> · ${tr(STATUS_BADGE[x.billing_status]?.[1] ?? x.billing_status ?? "—")}
      ${x.billing_status === "trialing" && ` · ${tr("until {date}", { date: new Date(x.trial_ends_at).toLocaleDateString(locale()) })}`}</p>
    ${paying
      ? html`<div class="callout small">${tr("This business pays by card. It changes its plan from its own billing page; you can still waive its setup fee here.")}</div>`
      : html`<section class="manage-block">
          <${Field} label=${tr("Plan")}><select ...${bind("plan")}>${PLAN_CHOICES.map((p) => html`<option value=${p}>${p}</option>`)}</select></${Field}>
          <div class="row">
            <button class="btn" disabled=${busy} onClick=${comp}>${tr("Make complimentary")}</button>
          </div>
          <p class="muted small">${tr("Complimentary: free on the chosen plan, no trial end. Founding is the early-access plan with no limits.")}</p>
          <div class="row align-end">
            <${Field} label=${x.billing_status === "trialing" ? tr("Extend trial by (days)") : tr("Start a trial of (days)")}><input type="number" min="1" max="90" ...${bind("days")} /></${Field}>
            <button class="btn" disabled=${busy} onClick=${trial}>${x.billing_status === "trialing" ? tr("Extend trial") : tr("Start trial")}</button>
          </div>
        </section>`}
    <section class="manage-block">
      <label class="toggle"><input type="checkbox" ...${check("waive")} /> ${tr("Waive the setup fee")}</label>
      <button class="btn small" disabled=${busy || !values.waive} onClick=${() => billing({ setup_fee_waived: true }, tr("Setup fee waived"))}>${tr("Apply")}</button>
    </section>
  </${Modal}>`;
}

/** Where the budget a business runs under comes from, in words: custom, the plan's default, or none. */
function budgetSource(x) {
  if (x.ai_monthly_cap_usd != null) return tr("Custom budget");
  if (x.ai_cap_default_usd != null) return tr("Plan default");
  if (x.own_ai_key) return tr("No budget: its own key");
  if (x.billing_status === "trialing") return tr("Trial credit applies");
  return tr("No budget");
}

/** Spend this month against the budget in force, and the one button that changes it. */
function BudgetCell({ x, onEdit }) {
  const cap = x.ai_monthly_cap_usd ?? x.ai_cap_default_usd;
  const over = cap != null && x.ai_cost_month_usd >= cap;
  return html`<div class="nowrap">
      <span class=${over ? "error-text" : ""}>$${x.ai_cost_month_usd.toFixed(2)}</span>
      <span class="muted small">${cap != null ? ` ${tr("of")} $${cap}` : ""}</span>
    </div>
    <div class="muted small">${budgetSource(x)}</div>
    <button class="btn small" onClick=${onEdit}>${tr("AI budget…")}</button>`;
}

/** The monthly AI budget of one business: follow the plan's default, or set a number. */
function AiBudget({ x, onClose, onDone }) {
  const [value, setValue] = useState(x.ai_monthly_cap_usd ?? "");
  const [busy, run] = useAction();
  const save = (cap) => run(() => patch(`/admin/tenants/${x.id}`, { ai_monthly_cap_usd: cap }), tr("Budget saved")).then(onDone);
  const submit = (e) => {
    e.preventDefault();
    const cap = String(value).trim() === "" ? null : Number(value);
    if (cap !== null && (!Number.isFinite(cap) || cap < 0)) return;
    save(cap);
  };
  const free = x.billing_status === "comped" || x.plan === "founding";
  const fallback = x.ai_cap_default_usd != null && free
    ? tr("Default: ${amount} a month, the allowance every complimentary and Founding business gets.", { amount: x.ai_cap_default_usd })
    : x.ai_cap_default_usd != null
    ? tr("Plan default: ${amount} a month, the most this plan can earn us in a month, so a paying business never reaches it in normal use.", { amount: x.ai_cap_default_usd })
    : x.own_ai_key
      ? tr("No default: it runs on its own Anthropic key, so Anthropic bills the business, not us.")
      : x.billing_status === "trialing"
        ? tr("No default during the trial: the trial's $5 AI credit limits what our key spends.")
        : tr("No default: this plan doesn't include AI usage on our key.");
  return html`<${Modal} title=${tr("AI budget for {name}", { name: x.name })} onClose=${onClose}>
    <form onSubmit=${submit}>
      <p class="small">${tr("The most AI may cost on this business in a calendar month. Once it is reached the agent stops answering, new chats go to the business's team, and the team gets one alert. It starts again on the 1st, or as soon as you raise it.")}</p>
      <p class="small">${tr("This month so far: ${amount}", { amount: x.ai_cost_month_usd.toFixed(2) })}</p>
      <p class="muted small">${fallback}</p>
      <${Field} label=${tr("Custom budget (USD a month)")} hint=${tr("Leave empty to follow the plan default.")}>
        <input type="number" min="0" step="1" placeholder=${x.ai_cap_default_usd ?? ""} value=${value} onInput=${(e) => setValue(e.target.value)} />
      </${Field}>
      <div class="form-actions">
        <button type="button" class="btn ghost" onClick=${onClose}>${tr("Back")}</button>
        ${x.ai_monthly_cap_usd != null && html`<button type="button" class="btn" disabled=${busy} onClick=${() => save(null)}>${tr("Use the plan default")}</button>`}
        <button class="btn primary" disabled=${busy}>${tr("Save budget")}</button>
      </div>
    </form>
  </${Modal}>`;
}

/** The platform's own Claude key, where it lives, and the budgets each case gets by default. */
function ClaudeKey() {
  const [data] = useLoad(() => get("/admin/ai"), []);
  if (!data) return null;
  return html`<section class="card">
    <h2>${tr("Claude API key")}</h2>
    <p class="small">${data.platform_key
      ? html`<${Badge} kind="ok">${tr("Key set")}</${Badge}> ${tr("ending in")} <code>${data.platform_key.hint}</code>`
      : html`<${Badge} kind="warn">${tr("Not set")}</${Badge}>`}</p>
    <p class="small">${tr("Our Anthropic key pays for trials, Growth, Pro and complimentary businesses. It is the Worker secret ANTHROPIC_API_KEY: in Cloudflare, Workers & Pages → whatsapp-schedule → Settings → Variables and Secrets. Create the key at console.anthropic.com.")}</p>
    <p class="muted small">${tr("A business with its own key adds it on its AI agent page (owners only). Lite and Starter need one once the trial ends.")}</p>
    <h3 class="small">${tr("Default monthly AI budgets")}</h3>
    <ul class="small">
      ${data.default_caps.map((d) => html`<li>${d.plan}: ${d.cap != null ? `$${d.cap.toLocaleString(locale())}` : tr("none, own key")}</li>`)}
      <li>${tr("Trial: ${amount} credit for the whole trial", { amount: data.trial_credit_usd })}</li>
    </ul>
  </section>`;
}

/** The platform's own Morning account, and moving to another one. */
function PaymentAccount() {
  const [data, reload] = useLoad(() => get("/admin/billing"), []);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, run] = useAction();
  if (!data) return null;
  const reset = async () => {
    const res = await run(() => post("/admin/billing/reset-cards", { confirm: typed }));
    setOpen(false);
    setTyped("");
    reload();
    alert(tr("{n} saved cards were removed. Paying businesses now see a request to add their card again.", { n: res.reset }));
  };
  return html`<section class="card">
    <h2>${tr("Payment account")}</h2>
    ${data.provider
      ? html`<p class="small"><${Badge} kind="ok">${tr("Morning connected")}</${Badge}> ${tr("key")} <code>${data.key_id}</code> · ${tr("plugin")} <code>${data.plugin_id ?? "—"}</code></p>
          <p class="muted small">${data.api_url}</p>`
      : html`<p class="small"><${Badge} kind="warn">${tr("Not connected")}</${Badge}> ${tr("Set MORNING_API_KEY_ID, MORNING_API_KEY_SECRET and MORNING_PLUGIN_ID as Worker secrets to take payments.")}</p>`}
    <p class="small">${tr("{paying} paying businesses · {cards} saved cards · {waiting} waiting for a new card", { paying: data.paying, cards: data.saved_cards, waiting: data.awaiting_card })}</p>
    <button class="btn small" onClick=${() => setOpen(true)}>${tr("Switch payment account…")}</button>
    ${open && html`<${Modal} title=${tr("Switch payment account")} onClose=${() => setOpen(false)}>
      <ol class="small">
        <li>${tr("In Cloudflare (Workers → whatsapp-schedule → Settings → Variables and Secrets), replace MORNING_API_KEY_ID, MORNING_API_KEY_SECRET and MORNING_PLUGIN_ID with the new Morning account's values.")}</li>
        <li>${tr("Then forget the saved cards here. They belong to the old account and can't be charged from the new one.")}</li>
        <li>${tr("Each paying business sees a request to add its card again before its next renewal. A renewal without a card fails and is retried as usual.")}</li>
      </ol>
      <${Field} label=${tr("Type RESET to confirm")}><input value=${typed} onInput=${(e) => setTyped(e.target.value)} /></${Field}>
      <div class="form-actions"><button class="btn ghost" onClick=${() => setOpen(false)}>${tr("Back")}</button>
        <button class="btn danger" disabled=${busy || typed !== "RESET"} onClick=${reset}>${tr("Forget saved cards")}</button></div>
    </${Modal}>`}
  </section>`;
}
